import type { Schemas } from '@suvoda-uat/contracts';
import { getAuthSession } from './auth.js';

/**
 * Typed HTTP client over the Smithy-generated contract types (no hand-written
 * shapes — Constitution §VI). Document bytes go straight to S3 via the
 * presigned PUT (R5); only metadata flows through these calls. Auth is the
 * Cognito ID token as `Authorization: Bearer` — identity AND roles are derived
 * server-side from the validated JWT (F-009, R6). The SPA sends NO actor or
 * api-key headers.
 */
export type Trial = Schemas['GetTrialResponseContent'];
export type TrialPack = Schemas['GetTrialPackResponseContent'];
export type MasterTemplateVersionInfo = Schemas['MasterTemplateVersionInfo'];
export type GenerationJob = Schemas['GetCurrentGenerationJobResponseContent'];
export type StartGenerationResult = Schemas['StartGenerationResponseContent'];
/** The generated Test Script for the UAT/Assembly view (F-002 US1). */
export type TestScript = Schemas['ListTestScriptResponseContent'];
/** A Section of the Test Script (F-002 + F-011). */
export type Section = Schemas['Section'];
/** A Subsection of a Section (F-002 + F-011). */
export type Subsection = Schemas['Subsection'];
/** Provenance of a Section/Subsection (F-011): 'generated' (absent) | 'novel'. */
export type NodeOrigin = Schemas['NodeOrigin'];
/** A single Test Case within the Test Script (F-002 US2). */
export type TestCase = Schemas['TestCase'];
/** F-017 (US2): the derived per-node assembly state + machine-generated reason. */
export type StructureState = Schemas['StructureState'];
/** F-017 (US2): one study-specific requirement awaiting human authorship. */
export type AuthoringQueueItem = Schemas['AuthoringQueueItem'];
/** F-017 (US2): the nearest historical precedent offered as a starting draft. */
export type PrecedentDraft = Schemas['PrecedentDraft'];
/** F-017 (US2): the body realising a queued item into a novel Test Case. */
export type AuthorQueueItemInput = Schemas['AuthorQueueItemRequestContent'];
/** A Test Case's served content: ordered Content Items + provenance metadata. */
export type TestCaseContent = Schemas['TestCaseContent'];
/** A single ordered Content Item as served (carries derived `origin`, F-008). */
export type ContentItem = Schemas['ContentItem'];
/** The edit-content input item: body + kind (+ echoed templateRef); NO origin (I24). */
export type ContentItemInput = Schemas['ContentItemInput'];
/** Structural role of a Content Item. */
export type ContentItemKind = Schemas['ContentItemKind'];
/** Provenance of a Content Item (derived, read-only). */
export type ContentItemOrigin = Schemas['ContentItemOrigin'];
/** Link to a disambiguated template item + its baseline (origin derivation/revert). */
export type TemplateRef = Schemas['TemplateRef'];
/** F-049: a user-applied inline emphasis (B/I/U) over a code-point span of a body. */
export type FormattingMark = Schemas['FormattingMark'];
/** F-049: a template-derived, read-only, never-exported editing-hint highlight span. */
export type EditingHintHighlight = Schemas['EditingHintHighlight'];
/** One suggested draft row for a novel case (carries `kind`). */
export type SuggestedRow = Schemas['SuggestedRow'];
/** F-013 (R11): the full per-render report for a finalised version. */
export type RenderReport = Schemas['GetRenderReportResponseContent'];
/** CreateSection request body (F-011): title + optional afterSectionId. */
export type CreateSectionInput = Schemas['CreateSectionRequestContent'];
/** CreateSubsection request body (F-011): title + optional afterSubsectionId. */
export type CreateSubsectionInput = Schemas['CreateSubsectionRequestContent'];
/** MoveSection request body (feature 015): optional afterSectionId (absent ⇒ top). */
export type MoveSectionInput = Schemas['MoveSectionRequestContent'];
/** MoveSubsection request body (feature 015): optional afterSubsectionId (absent ⇒ top). */
export type MoveSubsectionInput = Schemas['MoveSubsectionRequestContent'];
/** MoveTestCase request body (feature 015): required toSubsectionId + optional afterTestCaseId. */
export type MoveTestCaseInput = Schemas['MoveTestCaseRequestContent'];
/** One Team directory entry (feature 015, admin-only). */
export type TeamMember = Schemas['TeamMember'];
/** Minimal public identity (id/name/initials) — Trial owner/reviewer + the F-139 assignable list. */
export type UserSummary = Schemas['UserSummary'];
/** F-141: a Review on a Test Case (comment/flag) as served — newest-first in lists. */
export type Review = Schemas['Review'];
/** F-141: review kind discriminator ('comment' proposes content | 'flag' attention-only). */
export type ReviewType = Schemas['ReviewType'];
/**
 * F-141: the CreateReview body. A `comment` carries the proposed `rows` (the
 * editor's input shape — NO origin, server-derived) + the `baseVersion` they were
 * written against; a `flag` carries neither. An optional `note` rides either.
 */
export type CreateReviewBody = {
  readonly type: ReviewType;
  readonly rows?: readonly ContentItemInput[];
  readonly baseVersion?: number;
  readonly note?: string;
};
/** The PATCH body for replacing a Test Case's ordered Content Items. */
export type EditContentInput = Schemas['EditTestCaseContentRequestContent'];
/** The PUT body for setting or clearing a Test Case title. */
export type UpdateTitleInput = Schemas['UpdateTestCaseTitleRequestContent'];
/** The PUT body for setting or clearing a Test Case's Function (F-033). */
export type UpdateFunctionInput = Schemas['UpdateTestCaseFunctionRequestContent'];
/** The PUT body for setting/clearing a Test Case's study roles (F-010). */
export type SetRolesInput = Schemas['SetTestCaseRolesRequestContent'];
/** SuggestTestCaseRows request body — the (sensitive) title travels in the body (US4). */
export type SuggestRowsInput = Schemas['SuggestTestCaseRowsRequestContent'];
/** Availability-shaped row-suggestion result for a novel case (US4). */
export type RowSuggestionResult = Schemas['SuggestTestCaseRowsResponseContent'];
/** PrepareDownload's `{ result }` envelope — `available` or `notAvailable` (US3). */
export type PrepareDownloadResult = Schemas['PrepareDownloadResponseContent'];
/** PrepareInterimDownload's `{ result }` envelope — `available` (URL + counts) or `notAvailable` (F-149). */
export type PrepareInterimDownloadResult = Schemas['PrepareInterimDownloadResponseContent'];
export type FinaliseTrialResult = Schemas['FinaliseTrialResponseContent'];
export type SlotName = 'urs' | 'trs' | 'randomization-addendum' | 'roles-matrix';

export type FirstDocumentInput = {
  readonly slot: SlotName;
  readonly filename: string;
  readonly sizeBytes: number;
  readonly mimeType: string;
  readonly contentHash: string;
  readonly stagingKey: string;
};

export type CreateTrialInput = {
  readonly name: string;
  readonly masterTemplateVersion: string;
  /** F-020 (R1): the staged first document the server adopts at create. */
  readonly firstDocument: FirstDocumentInput;
};

export type StageTrialDocumentInput = {
  readonly slot: SlotName;
  readonly filename: string;
  readonly sizeBytes: number;
  readonly mimeType: string;
  readonly contentHash: string;
};

export type PutSlotInput = {
  readonly filename: string;
  readonly sizeBytes: number;
  readonly mimeType: string;
  readonly contentHash: string;
  readonly storageUri: string;
};

const baseUrl = (): string => {
  const value = import.meta.env['VITE_API_URL'];
  if (!value) throw new Error('VITE_API_URL must be set to the deployed API URL.');
  return value;
};
/**
 * Headers sent on every request: ONLY the Cognito bearer (ID) token when the
 * browser has a session. The server derives identity + roles from the validated
 * JWT and ignores/strips any client-supplied actor headers (R6), so the SPA
 * sends neither `x-actor-*` nor `x-api-key`. Read per-call so tests can stub
 * session state.
 */
const baseHeaders = (): Record<string, string> => {
  const session = getAuthSession();
  return session === undefined ? {} : { authorization: `Bearer ${session.idToken}` };
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    detail: string,
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${baseUrl()}${path}`, {
    ...init,
    headers: { ...baseHeaders(), ...init.headers },
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { detail?: string };
    throw new ApiError(response.status, problem.detail ?? `Request failed (${response.status}).`);
  }
  return response.json() as Promise<T>;
};

const jsonHeaders = (extra: Record<string, string> = {}): Record<string, string> => ({
  'content-type': 'application/json',
  ...extra,
});

/**
 * Optimistic-concurrency + idempotency headers for a designer mutation: the
 * resource `version` is sent as `If-Match`, and an optional `Idempotency-Key`
 * lets the server de-dup a retried POST (mirrors `putSlot`/`startGeneration`).
 */
const mutationHeaders = (
  version: number,
  idempotencyKey?: string,
  extra: Record<string, string> = {},
): Record<string, string> => ({
  'if-match': String(version),
  ...(idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey }),
  ...extra,
});

const testCasePath = (trialId: string, testCaseId: string): string =>
  `/trials/${trialId}/test-script/test-cases/${testCaseId}`;

export const listMasterTemplateVersions = (): Promise<{ items: MasterTemplateVersionInfo[] }> =>
  request('/master-templates');

export const createTrial = (input: CreateTrialInput, idempotencyKey?: string): Promise<Trial> =>
  request('/trials', {
    method: 'POST',
    headers: jsonHeaders(idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(input),
  });

/**
 * F-020 (R1): trial-less staging presign for the FIRST document of a
 * not-yet-created project. The returned stagingKey is what CreateTrial adopts.
 */
export const stageTrialDocument = (
  input: StageTrialDocumentInput,
): Promise<{ stagingKey: string; uploadUrl: string }> =>
  request('/pack/stage', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });

/**
 * F-020 (R5): correct the derived study name. Marks the name user-set —
 * after this no system derivation may overwrite it (FR-005).
 */
export const renameTrial = (trialId: string, name: string): Promise<Trial> =>
  request(`/trials/${trialId}/name`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });

/**
 * F-139 (R1): reassign a Trial's Owner after creation. Mirrors renameTrial — a
 * narrow PATCH on the owner subresource. Returns the updated Trial.
 */
export const setTrialOwner = (trialId: string, ownerId: string): Promise<Trial> =>
  request(`/trials/${trialId}/owner`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ ownerId }),
  });

/**
 * F-033 (R6/FR-015): accept a provisional system-derived name unchanged. Marks
 * it user-set and clears the needs-confirmation cue. No body.
 */
export const confirmTrialName = (trialId: string): Promise<Trial> =>
  request(`/trials/${trialId}/name/confirm`, { method: 'POST', headers: jsonHeaders() });

/** Minimal Dashboard list — the acting user's Trials (T053). */
export const listTrials = (): Promise<{ items: Trial[] }> => request('/trials');

export const getTrial = (trialId: string): Promise<Trial> => request(`/trials/${trialId}`);

export const getTrialPack = (trialId: string): Promise<TrialPack> =>
  request(`/trials/${trialId}/pack`);

/** Read the generated Test Script (Sections → Subsections → Test Cases) for a Trial (US1). */
export const listTestScript = (trialId: string): Promise<TestScript> =>
  request(`/trials/${trialId}/test-script`);

/** Every account + mapped app roles (feature 015 — admin-only; non-admins 403). */
export const listTeamMembers = (): Promise<{ items: TeamMember[] }> => request('/team/members');

/**
 * F-139 (R7): the workspace users assignable as a Trial's Owner — enabled,
 * authoring-capable accounts as `UserSummary` (id/name/initials). Editor-tier
 * (distinct from the admin-only listTeamMembers); fetched once + shared.
 */
export const listAssignableUsers = (): Promise<{ items: UserSummary[] }> =>
  request('/assignable-users');

export const createTestCase = (
  trialId: string,
  subsectionId: string,
  idempotencyKey: string,
): Promise<TestCase> =>
  request(`/trials/${trialId}/test-script/subsections/${subsectionId}/test-cases`, {
    method: 'POST',
    headers: { 'idempotency-key': idempotencyKey },
  });

/** Insert a novel Section (F-011). `afterSectionId` absent ⇒ insert at the top. */
export const createSection = (
  trialId: string,
  body: CreateSectionInput,
  idempotencyKey: string,
): Promise<Section> =>
  request(`/trials/${trialId}/test-script/sections`, {
    method: 'POST',
    headers: jsonHeaders({ 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(body),
  });

/** Insert a novel Subsection into a Section (F-011). `afterSubsectionId` absent ⇒ top. */
export const createSubsection = (
  trialId: string,
  sectionId: string,
  body: CreateSubsectionInput,
  idempotencyKey: string,
): Promise<Subsection> =>
  request(`/trials/${trialId}/test-script/sections/${sectionId}/subsections`, {
    method: 'POST',
    headers: jsonHeaders({ 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(body),
  });

/** Move a Section among its siblings (feature 015). `afterSectionId` absent ⇒ top. */
export const moveSection = (
  trialId: string,
  sectionId: string,
  body: MoveSectionInput,
  idempotencyKey: string,
): Promise<Section> =>
  request(`/trials/${trialId}/test-script/sections/${sectionId}/move`, {
    method: 'POST',
    headers: jsonHeaders({ 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(body),
  });

/** Move a Subsection within its own Section (feature 015). */
export const moveSubsection = (
  trialId: string,
  subsectionId: string,
  body: MoveSubsectionInput,
  idempotencyKey: string,
): Promise<Subsection> =>
  request(`/trials/${trialId}/test-script/subsections/${subsectionId}/move`, {
    method: 'POST',
    headers: jsonHeaders({ 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(body),
  });

/** Move a Test Case within or between Subsections (feature 015). */
export const moveTestCase = (
  trialId: string,
  testCaseId: string,
  body: MoveTestCaseInput,
  idempotencyKey: string,
): Promise<TestCase> =>
  request(`/trials/${trialId}/test-script/test-cases/${testCaseId}/move`, {
    method: 'POST',
    headers: jsonHeaders({ 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(body),
  });

/**
 * Run the FR-013 export/download capability — POST …/prepare-download. Returns
 * the `{ result }` union envelope: `available` (a presigned DOCX URL for the
 * latest finalised version) or `notAvailable` (current refusal facts). An
 * optional `Idempotency-Key` lets the server de-dup a retry.
 */
export const prepareDownload = (
  trialId: string,
  idempotencyKey?: string,
): Promise<PrepareDownloadResult> =>
  request(`/trials/${trialId}/test-script/prepare-download`, {
    method: 'POST',
    headers: idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey },
  });

/**
 * F-149: prepare an interim (draft) download — renders the in-review trial's
 * Included + Complete cases to a transient artefact and returns a presigned URL
 * plus the outstanding-work counts (or a `notAvailable` reason). The
 * Idempotency-Key header is optional/latent (mirrors {@link prepareDownload}).
 */
export const prepareInterimDownload = (
  trialId: string,
  idempotencyKey?: string,
): Promise<PrepareInterimDownloadResult> =>
  request(`/trials/${trialId}/test-script/prepare-interim-download`, {
    method: 'POST',
    headers: idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey },
  });

/**
 * F-013 (R11, FR-016): the full Render Report for a finalised version —
 * warnings + the unresolved-placeholder enumeration + render stats.
 */
export const getRenderReport = (trialId: string, version: string): Promise<RenderReport> =>
  request(`/trials/${trialId}/finalised/${encodeURIComponent(version)}/render-report`);

export const finaliseTrial = (
  trialId: string,
  version: string,
  idempotencyKey?: string,
): Promise<FinaliseTrialResult> =>
  request(`/trials/${trialId}/finalise`, {
    method: 'POST',
    headers: jsonHeaders(idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey }),
    body: JSON.stringify({ version }),
  });

export const createUploadUrl = (
  trialId: string,
  slot: SlotName,
  contentType: string,
  contentHash: string,
): Promise<{ url: string; storageUri: string }> =>
  request(`/trials/${trialId}/pack/${slot}/upload-url`, {
    method: 'POST',
    headers: jsonHeaders(),
    // The hash is signed into the presign as object metadata (real S3 rejects
    // an unsigned x-amz-meta-* header), so it must be known before presigning.
    body: JSON.stringify({ contentType, contentHash }),
  });

export const putSlot = (
  trialId: string,
  slot: SlotName,
  input: PutSlotInput,
  version: number,
): Promise<TrialPack> =>
  request(`/trials/${trialId}/pack/${slot}`, {
    method: 'PUT',
    headers: jsonHeaders({ 'if-match': String(version) }),
    body: JSON.stringify(input),
  });

export const deleteSlot = (trialId: string, slot: SlotName, version: number): Promise<TrialPack> =>
  request(`/trials/${trialId}/pack/${slot}`, {
    method: 'DELETE',
    headers: { 'if-match': String(version) },
  });

/** Persist the per-trial randomization toggle (F-001 SetRandomizationEnabled, exposed by F-021 US3). */
export const setRandomizationEnabled = (
  trialId: string,
  randomizationEnabled: boolean,
  version: number,
): Promise<TrialPack> =>
  request(`/trials/${trialId}/pack`, {
    method: 'PATCH',
    headers: jsonHeaders({ 'if-match': String(version) }),
    body: JSON.stringify({ randomizationEnabled }),
  });

/** Start (or re-run) generation — 202 (R12: an Idempotency-Key replays the job). */
export const startGeneration = (
  trialId: string,
  idempotencyKey?: string,
): Promise<StartGenerationResult> =>
  request(`/trials/${trialId}/generation-jobs`, {
    method: 'POST',
    headers: idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey },
  });

/** Poll the current generation job for the Verification view (R6). */
export const getCurrentGenerationJob = (trialId: string): Promise<GenerationJob> =>
  request(`/trials/${trialId}/generation-jobs/current`);

/** Request a best-effort cancellation — 202, returns the (flagged) job (R2, If-Match-exempt). */
export const cancelGeneration = (trialId: string, jobId: string): Promise<GenerationJob> =>
  request(`/trials/${trialId}/generation-jobs/${jobId}/cancel`, { method: 'POST' });

/** Override a Test Case's Inclusion Decision to `included` (FR-013). */
const sectionPath = (trialId: string, sectionId: string): string =>
  `/trials/${trialId}/test-script/sections/${sectionId}`;

const subsectionPath = (trialId: string, subsectionId: string): string =>
  `/trials/${trialId}/test-script/subsections/${subsectionId}`;

/**
 * F-013 (FR-010): Instructional-Section Decision writes — six clones of the
 * test-case triple at the node levels. If-Match carries the AGGREGATE
 * (TestScriptContent) version; the response is the decided node.
 */
export const includeSection = (
  trialId: string,
  sectionId: string,
  version: number,
): Promise<Section> =>
  request(`${sectionPath(trialId, sectionId)}/include`, {
    method: 'POST',
    headers: mutationHeaders(version),
  });

export const excludeSection = (
  trialId: string,
  sectionId: string,
  version: number,
): Promise<Section> =>
  request(`${sectionPath(trialId, sectionId)}/exclude`, {
    method: 'POST',
    headers: mutationHeaders(version),
  });

export const clearSectionInclusion = (
  trialId: string,
  sectionId: string,
  version: number,
): Promise<Section> =>
  request(`${sectionPath(trialId, sectionId)}/clear-inclusion`, {
    method: 'POST',
    headers: mutationHeaders(version),
  });

export const includeSubsection = (
  trialId: string,
  subsectionId: string,
  version: number,
): Promise<Subsection> =>
  request(`${subsectionPath(trialId, subsectionId)}/include`, {
    method: 'POST',
    headers: mutationHeaders(version),
  });

export const excludeSubsection = (
  trialId: string,
  subsectionId: string,
  version: number,
): Promise<Subsection> =>
  request(`${subsectionPath(trialId, subsectionId)}/exclude`, {
    method: 'POST',
    headers: mutationHeaders(version),
  });

export const clearSubsectionInclusion = (
  trialId: string,
  subsectionId: string,
  version: number,
): Promise<Subsection> =>
  request(`${subsectionPath(trialId, subsectionId)}/clear-inclusion`, {
    method: 'POST',
    headers: mutationHeaders(version),
  });

/**
 * F-113 (US3): edit a subsection's General Instructions — PATCH …/instructions.
 * The body carries the new `body` (+ optional `reason`); the server drops the
 * template highlights (FR-008). If-Match carries the AGGREGATE (TestScriptContent)
 * version; the response is the updated subsection.
 */
export const updateSubsectionInstructions = (
  trialId: string,
  subsectionId: string,
  body: string,
  version: number,
  reason?: string,
  idempotencyKey?: string,
): Promise<Subsection> =>
  request(`${subsectionPath(trialId, subsectionId)}/instructions`, {
    method: 'PATCH',
    headers: jsonHeaders(mutationHeaders(version, idempotencyKey)),
    body: JSON.stringify({ body, ...(reason === undefined ? {} : { reason }) }),
  });

export const includeTestCase = (
  trialId: string,
  testCaseId: string,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/include`, {
    method: 'POST',
    headers: mutationHeaders(version, idempotencyKey),
  });

/** Override a Test Case's Inclusion Decision to `excluded` (FR-013). */
export const excludeTestCase = (
  trialId: string,
  testCaseId: string,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/exclude`, {
    method: 'POST',
    headers: mutationHeaders(version, idempotencyKey),
  });

/** Mark a Test Case as Complete (FR-015) — rejected with 422 if no Role assigned. */
export const markComplete = (
  trialId: string,
  testCaseId: string,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/mark-complete`, {
    method: 'POST',
    headers: mutationHeaders(version, idempotencyKey),
  });

/** Reopen (deselect "Mark as Complete") a Test Case (F-038 US2/FR-005) — the
 * reversible inverse of markComplete; returns the case to incomplete. */
export const reopenTestCase = (
  trialId: string,
  testCaseId: string,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/reopen`, {
    method: 'POST',
    headers: mutationHeaders(version, idempotencyKey),
  });

/**
 * F-017 (US3): the occurrence-decision ops — confirm a flagged variant default
 * (one click, SC-013) or override an applicability decision (reason required,
 * FR-009). The path has NO `/test-script/` segment (contract) and
 * `expectedVersion` rides in the BODY, NOT the If-Match header.
 */
const decisionPath = (trialId: string, testCaseId: string, action: string): string =>
  `/trials/${trialId}/test-cases/${testCaseId}/decision/${action}`;

export const confirmOccurrenceDecision = (
  trialId: string,
  testCaseId: string,
  expectedVersion: number,
): Promise<{ testCase: TestCase }> =>
  request(decisionPath(trialId, testCaseId, 'confirm'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ expectedVersion }),
  });

export const overrideOccurrenceDecision = (
  trialId: string,
  testCaseId: string,
  input: { readonly applicable: boolean; readonly reason: string },
  expectedVersion: number,
): Promise<{ testCase: TestCase }> =>
  request(decisionPath(trialId, testCaseId, 'override'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ ...input, expectedVersion }),
  });

/**
 * F-017 (US2): the Authoring Queue REST surface — the honestly-labelled home
 * for study-specific scope. Reads are viewer-tier; the four transitions are
 * editor-tier and machine-guarded (§XI). `expectedVersion` rides in the BODY
 * (the queue item's optimistic-concurrency guard), NOT the If-Match header.
 */
export const listAuthoringQueue = (trialId: string): Promise<{ items: AuthoringQueueItem[] }> =>
  request(`/trials/${trialId}/authoring-queue`);

const authoringQueuePath = (trialId: string, itemId: string, action: string): string =>
  `/trials/${trialId}/authoring-queue/${itemId}/${action}`;

export const startAuthoringDraft = (
  trialId: string,
  itemId: string,
  expectedVersion: number,
): Promise<{ item: AuthoringQueueItem }> =>
  request(authoringQueuePath(trialId, itemId, 'start'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ expectedVersion }),
  });

/** Realise a queued item into a novel Test Case via the existing CreateTestCase path. */
export const authorQueueItem = (
  trialId: string,
  itemId: string,
  input: AuthorQueueItemInput,
): Promise<{ item: AuthoringQueueItem; testCase: TestCase }> =>
  request(authoringQueuePath(trialId, itemId, 'author'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });

export const dismissQueueItem = (
  trialId: string,
  itemId: string,
  reason: string,
  expectedVersion: number,
): Promise<{ item: AuthoringQueueItem }> =>
  request(authoringQueuePath(trialId, itemId, 'dismiss'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ reason, expectedVersion }),
  });

export const restoreQueueItem = (
  trialId: string,
  itemId: string,
  expectedVersion: number,
): Promise<{ item: AuthoringQueueItem }> =>
  request(authoringQueuePath(trialId, itemId, 'restore'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ expectedVersion }),
  });

/** Clear a Test Case's Inclusion Decision back to `undecided` (FR-013) — reverts the row to the AI Recommendation alone. */
export const clearInclusion = (
  trialId: string,
  testCaseId: string,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/clear-inclusion`, {
    method: 'POST',
    headers: mutationHeaders(version, idempotencyKey),
  });

/**
 * Edit a Test Case's content (FR-020) — PATCH …/content. The 0.3.0 body is a
 * full ordered row replacement (`{ rows, reason? }`), sent with the optimistic
 * `If-Match` version and an optional `Idempotency-Key`.
 */
export const editContent = (
  trialId: string,
  testCaseId: string,
  content: EditContentInput,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/content`, {
    method: 'PATCH',
    headers: jsonHeaders(mutationHeaders(version, idempotencyKey)),
    body: JSON.stringify(content),
  });

/**
 * F-141 (#141): list the reviews on a Test Case, newest-first (the server's SK
 * order). Viewer-tier — anyone who can view the trial reads them; an empty case
 * lists `{ items: [] }`, not an error.
 */
export const listReviews = (trialId: string, testCaseId: string): Promise<{ items: Review[] }> =>
  request(`${testCasePath(trialId, testCaseId)}/reviews`);

/**
 * F-141 (#141): attach a Review to a Test Case (non-destructive). A `comment`
 * proposes the complete replacement `rows` + `baseVersion`; a `flag` only raises
 * attention. Editor-tier AND non-owner-only — the server refuses the trial owner
 * (403) and viewers (403). Idempotency-Key de-dups a retried POST.
 */
export const createReview = (
  trialId: string,
  testCaseId: string,
  body: CreateReviewBody,
  idempotencyKey?: string,
): Promise<{ review: Review }> =>
  request(`${testCasePath(trialId, testCaseId)}/reviews`, {
    method: 'POST',
    headers: jsonHeaders(idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey }),
    body: JSON.stringify(body),
  });

/**
 * Import an existing case's content into the target (F-031, UAT-27) — POST
 * …/import. The body carries ONLY the SOURCE case id; the server reads its rows
 * authoritatively and appends a verbatim copy onto the target, returning the
 * updated TARGET case. Sent with the optimistic `If-Match` (target version) and
 * an optional `Idempotency-Key`.
 */
export const importTestCase = (
  trialId: string,
  testCaseId: string,
  sourceTestCaseId: string,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/import`, {
    method: 'POST',
    headers: jsonHeaders(mutationHeaders(version, idempotencyKey)),
    body: JSON.stringify({ sourceTestCaseId }),
  });

/**
 * Update a Test Case's display title (FR-005) — PUT …/title. Omitting `title`
 * from the body clears it and restores the function/Test-ID fallback.
 */
export const updateTitle = (
  trialId: string,
  testCaseId: string,
  body: UpdateTitleInput,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/title`, {
    method: 'PUT',
    headers: jsonHeaders(mutationHeaders(version, idempotencyKey)),
    body: JSON.stringify(body),
  });

/**
 * Set, change, or clear a Test Case's Function (F-033 FR-008) — PUT …/function.
 * Omitting `function` from the body clears it (restores the 'Unassigned'
 * placeholder). An effective edit on a finalised trial regresses it (FR-013).
 */
export const updateFunction = (
  trialId: string,
  testCaseId: string,
  body: UpdateFunctionInput,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/function`, {
    method: 'PUT',
    headers: jsonHeaders(mutationHeaders(version, idempotencyKey)),
    body: JSON.stringify(body),
  });

/**
 * Set, change, or clear a Test Case's study roles (F-010 FR-012) — PUT …/roles.
 * Full-list replace; `roles: []` clears. The server validates each role against
 * the trial's Allowed-Role Set (422 on an out-of-matrix role) and regresses a
 * finalised trial on an effective change (FR-020).
 */
export const setRoles = (
  trialId: string,
  testCaseId: string,
  body: SetRolesInput,
  version: number,
  idempotencyKey?: string,
): Promise<TestCase> =>
  request(`${testCasePath(trialId, testCaseId)}/roles`, {
    method: 'PUT',
    headers: jsonHeaders(mutationHeaders(version, idempotencyKey)),
    body: JSON.stringify(body),
  });

/**
 * Suggest draft rows for a novel case (FR-015) — POST …/row-suggestion. The
 * sensitive title travels in the body (POST-as-query, §IX). Read-only and
 * draft-only: no `If-Match`/`Idempotency-Key`, nothing is persisted server-side.
 */
export const suggestTestCaseRows = (
  trialId: string,
  testCaseId: string,
  input: SuggestRowsInput,
): Promise<RowSuggestionResult> =>
  request(`${testCasePath(trialId, testCaseId)}/row-suggestion`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });

// ── Feature 016 (Cowork Companion & source relevance) ───────────────────────

export type SourceSectionCard = {
  sectionId: string;
  path: string;
  heading: string;
  excerpt: string;
  score: number;
};
export type SourceSettingsMatch = { parameter: string; value: string; score: number };
export type RelatedSectionsEnvelope = {
  state: 'ready' | 'preparing' | 'failed' | 'unavailable';
  sections: SourceSectionCard[];
  settingsMatches: SourceSettingsMatch[];
};
export type SourceSection = {
  sectionId: string;
  path: string;
  heading: string;
  text: string;
  part?: number;
};

/** Top-N relevant URS sections + TRS settings matches for one Test Case (US4).
 *  `preparing` means the per-trial source index is building — poll. */
export const listRelatedSections = (
  trialId: string,
  testCaseId: string,
): Promise<RelatedSectionsEnvelope> =>
  request(`/trials/${trialId}/test-script/test-cases/${testCaseId}/related-sections`);

/** Full text of one Source Section (the reading modal, FR-028). */
export const getSourceSection = (
  trialId: string,
  sectionId: string,
): Promise<{ section: SourceSection }> =>
  request(`/trials/${trialId}/source-sections/${sectionId}`);
